import { apiFetch } from "./http"
import { mockClient } from "./mockClient"
import type {
  DemoThreadPayload,
  OnboardingBootstrapResponse,
  OnboardingPrestartResponse,
} from "../types"

type PrestartPayload = {
  firstName: string
  lastName: string
  businessName?: string | null
  email: string
  phone?: string | null
}

export async function prestartOnboarding(demo: boolean, payload: PrestartPayload): Promise<OnboardingPrestartResponse> {
  if (demo) {
    return mockClient.onboardingPrestart(payload)
  }
  return apiFetch<OnboardingPrestartResponse>("/api/onboarding/prestart", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}

export async function resendOnboardingEmail(demo: boolean, preUserId: string): Promise<void> {
  if (demo) {
    await mockClient.onboardingResend(preUserId)
    return
  }
  await apiFetch("/api/onboarding/resend-email", {
    method: "POST",
    body: JSON.stringify({ preUserId }),
  })
}

export async function fetchOnboardingBootstrap(
  demo: boolean,
  params: { preUserId?: string; forceVariant?: string } = {},
): Promise<OnboardingBootstrapResponse> {
  if (demo) {
    return mockClient.getOnboardingBootstrap(params)
  }
  const query = new URLSearchParams()
  if (params.preUserId) query.set("preUserId", params.preUserId)
  if (params.forceVariant) query.set("forceVariant", params.forceVariant)
  const path = `/api/onboarding/bootstrap${query.toString() ? `?${query}` : ""}`
  return apiFetch<OnboardingBootstrapResponse>(path)
}

export async function fetchOnboardingDemoThread(demo: boolean): Promise<DemoThreadPayload> {
  if (demo) {
    return mockClient.getOnboardingDemoThread()
  }
  return apiFetch<DemoThreadPayload>("/api/demo/thread")
}

export async function requestStripeConnectLink(demo: boolean, token?: string): Promise<string> {
  if (demo) {
    return mockClient.getOnboardingStripeLink()
  }
  if (!token) {
    throw new Error("auth_token_required")
  }
  const { url } = await apiFetch<{ url: string }>("/api/onboarding/stripe-link", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  })
  return url
}

export type AfterClerkSignupResponse = {
  tenantId: number
  twilioNumber?: string | null
  twilioStatus: string
}

type AfterClerkSignupPayload = {
  email?: string | null
  phone?: string | null
  businessName?: string | null
  firstName?: string | null
  lastName?: string | null
  country?: string
}

export async function afterClerkSignup(
  token: string,
  payload: AfterClerkSignupPayload,
  demo = false,
): Promise<AfterClerkSignupResponse> {
  if (demo) {
    return {
      tenantId: 1,
      twilioNumber: "+15555550123",
      twilioStatus: "active",
    }
  }
  return apiFetch<AfterClerkSignupResponse>("/api/onboarding/after-clerk-signup", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  })
}

export async function saveOnboardingA2P(demo: boolean, fields: Record<string, unknown>): Promise<void> {
  if (demo) {
    await mockClient.saveOnboardingA2P(fields)
    return
  }
  await apiFetch("/api/onboarding/a2p/save", {
    method: "POST",
    body: JSON.stringify({ fields }),
  })
}

type VerificationStrategy = "session" | "lookup"

export type SyncVerificationOptions = {
  strategy?: VerificationStrategy
  preUserId?: string
  email?: string
  signUpId?: string
}

export type SyncVerificationResult = {
  provisioned: boolean
  emailVerified: boolean
  phoneVerified: boolean
}

export async function syncOnboardingVerification(
  demo: boolean,
  token?: string,
  options?: SyncVerificationOptions,
): Promise<SyncVerificationResult> {
  if (demo) {
    return { provisioned: true, emailVerified: true, phoneVerified: true }
  }
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
  const params = new URLSearchParams()
  if (options?.strategy && options.strategy !== "session") {
    params.set("strategy", options.strategy)
  }
  if (options?.preUserId) {
    params.set("preUserId", options.preUserId)
  }
  if (options?.email) {
    params.set("email", options.email)
  }
  if (options?.signUpId) {
    params.set("signUpId", options.signUpId)
  }
  const query = params.toString()
  return apiFetch<SyncVerificationResult>(`/api/onboarding/verification/sync${query ? `?${query}` : ""}`, {
    method: "POST",
    headers,
  })
}

export async function completeOnboarding(token: string, payload: { businessName: string, firstName: string, lastName: string, persona: string }): Promise<void> {
    await apiFetch("/api/onboarding/complete", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
    })
}

export async function getOnboardingStatus(token: string): Promise<{ status: string }> {
    return apiFetch<{ status: string }>("/api/onboarding/status", {
        headers: { Authorization: `Bearer ${token}` }
    })
}

export type TenantMeResponse = {
  id: number
  name?: string
  primary_phone?: string
  twilio_number?: string
  twilio_number_status?: string
  stripe_onboarding_status?: string
  default_persona?: string
}

export async function getTenantMe(token: string): Promise<TenantMeResponse> {
  return apiFetch<TenantMeResponse>("/api/tenants/me", {
    headers: { Authorization: `Bearer ${token}` },
  })
}
